import React, {useState, useContext, useEffect} from 'react';
import './StoenProjectForm.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {
  Button,
  CheckboxInput,
  DateInput,
  Loader,
  LoaderFullPage,
  Plus,
  SelectInput,
  TextInput,
  SectionBox
} from '../_common';
import {
  getCurrentUserData,
  getNestedPropertyValue,
  getOptionsList,
  setNestedPropertyValue
} from "../../helpers/helpers";
import ChangeHistory from "./ChangeHistory";
import _ from "lodash";
import {ToastContext} from "../../contexts/toastContext";
import {SelectionsContext} from "../../contexts/selectionsContext";
const StoenProjectForm = (
  {
    project,
    users,
    primaryButtonAction,
    secondaryButtonAction,
    preview,
    refreshProjectsList,
    setDataAsEdited,
    refreshSelectedProject
  }
) => {
  const { t } = useContext(I18nContext);
  const { displayToast } = useContext(ToastContext);
  const {
    addProjectPending,
    updateStoenProject,
    getSingleStoenProject,
    error,
  } = useContext(ProjectsContext);
  const { pending, getSelections, selections } = useContext(SelectionsContext);

  useEffect(() => {
    getSelections();
  }, []);

  const usersOptions = users.map(user => ({id: user.id, label: user.firstName + " " + user.lastName}));
  const subcompanyOptions = getOptionsList(selections, 'Firma Wolte');

  const [formValues, setFormValues] = useState({
    companyId: project?.companyId ?? "",
    googleDriveLink: project?.googleDriveLink ?? "",
    investmentAddress: project?.investmentAddress ?? '',
    orderNumber: project?.orderNumber ?? '',
    conditionsNumber: project?.conditionsNumber ?? '',
    designers: project
      ? project.designers
      : [getCurrentUserData().id],
    originalDueDate: project?.originalDueDate ?? '',
    dueDate: project?.dueDate ?? '',
    investmentSupervisor: project?.investmentSupervisor ?? '',
    cost: project?.cost ?? '',
    clientContactDetails: {
      address: project?.clientContactDetails?.address ?? '',
      phoneNumber: project?.clientContactDetails?.phoneNumber ?? '',
      email: project?.clientContactDetails?.email ?? '',
      fullName: project?.clientContactDetails?.fullName ?? ''
    },
    comments: project?.comments ?? '',
    appendices: project
      ? [...project?.appendices.filter(item => !!item.appendixDate && !!item.dueDate)]
      : [],
    partialProjectAcceptance: {
      complete: project?.partialProjectAcceptance?.complete ?? false,
      date: project?.partialProjectAcceptance?.date ?? '',
      comment: project?.partialProjectAcceptance?.comment ?? '',
    },
    networkStandards: {
      complete: project?.networkStandards?.complete ?? false,
      date: project?.networkStandards?.date ?? '',
      comment: project?.networkStandards?.comment ?? '',
    },
    stoenProject: {
      geodesy: {
        groundsRegistryExtract: {
          inDate: project?.stoenProject?.geodesy?.groundsRegistryExtract?.inDate ?? '',
          outDate: project?.stoenProject?.geodesy?.groundsRegistryExtract?.outDate ?? ''
        },
        baseMap: {
          inDate: project?.stoenProject?.geodesy?.baseMap?.inDate ?? '',
          outDate: project?.stoenProject?.geodesy?.baseMap?.outDate ?? ''
        },
        projectMap: {
          inDate: project?.stoenProject?.geodesy?.projectMap?.inDate ?? '',
          outDate: project?.stoenProject?.geodesy?.projectMap?.outDate ?? '',
          geodesyComment: project?.stoenProject?.geodesy?.projectMap?.geodesyComment ?? ''
        }
      },
      inventory: {
        inDate: project?.stoenProject?.inventory?.inDate ?? '',
        outDate: project?.stoenProject?.inventory?.outDate ?? '',
      },
      clientLocationAgreement: {
        inDate: project?.stoenProject?.clientLocationAgreement?.inDate ?? '',
        outDate: project?.stoenProject?.clientLocationAgreement?.outDate ?? '',
        comment: project?.stoenProject?.clientLocationAgreement?.comment ?? '',
        receivedOriginal: {
          complete: project?.stoenProject?.clientLocationAgreement?.receivedOriginal?.complete ?? false,
          date: project?.stoenProject?.clientLocationAgreement?.receivedOriginal?.date ?? '',
        },
        receivedAdaptationProject: {
          complete: project?.stoenProject?.clientLocationAgreement?.receivedAdaptationProject?.complete ?? false,
          date: project?.stoenProject?.clientLocationAgreement?.receivedAdaptationProject?.date ?? '',
        }
      },
      stoenLocationAgreement: {
        comment: project?.stoenProject?.stoenLocationAgreement?.comment ?? '',
        inDate: project?.stoenProject?.stoenLocationAgreement?.inDate ?? '',
        outDate: project?.stoenProject?.stoenLocationAgreement?.outDate ?? '',
      },
      zud: {
        inDate: project?.stoenProject?.zud?.inDate ?? '',
        outDate: project?.stoenProject?.zud?.outDate ?? '',
        details: project
          ? [...project?.stoenProject?.zud?.details]
          : [{name: '', inDate: '', outDate: '', comment: ''}],
      },
      rightsToTheLocation: project
        ? [...project?.stoenProject?.rightsToTheLocation]
        : [{parcelNumber: '', documentType: '', inDate: '', outDate: ''}],
      por: project
        ? [...project?.stoenProject?.por]
        : [{inDate: '', outDate: '', projectRecipient: ''}],
      additionalAgreements: {
        restorer: {
          inDate: project?.stoenProject?.additionalAgreements?.restorer?.inDate ?? '',
          outDate: project?.stoenProject?.additionalAgreements?.restorer?.outDate ?? '',
        },
        guarantee: {
          inDate: project?.stoenProject?.additionalAgreements?.guarantee?.inDate ?? '',
          outDate: project?.stoenProject?.additionalAgreements?.guarantee?.outDate ?? '',
          name: project?.stoenProject?.additionalAgreements?.guarantee?.name ?? ''
        },
        surfaceRestorationProject: {
          inDate: project?.stoenProject?.additionalAgreements?.guarantee?.inDate ?? '',
          outDate: project?.stoenProject?.additionalAgreements?.guarantee?.outDate ?? '',
        },
        additional: project
          ? [...project?.stoenProject?.additionalAgreements.additional]
          : [{comment: '', inDate: '', outDate: ''}],
      },
      landRightsDeclaration: {
        inDate: project?.stoenProject?.landRightsDeclaration?.inDate ?? '',
        outDate: project?.stoenProject?.landRightsDeclaration?.outDate ?? '',
      },
      projectAgreement: {
        side: project?.stoenProject?.projectAgreement?.side ?? '',
        inDate: project?.stoenProject?.projectAgreement?.inDate ?? '',
        outDate: project?.stoenProject?.projectAgreement?.outDate ?? '',
        comments: project
          ? [...project?.stoenProject?.projectAgreement.comments]
          : [{comment: '', inDate: '', outDate: ''}],
      }
    },
    projectStateChanges: project ? project.projectStateChanges : []
  });

  useEffect(() => {
    if (project) {
      setFormValues(project);
    }
  }, [project]);

  const changeValue = async (name, value, multiple, blur) => {
    setDataAsEdited();
    const newValues = {...formValues};

    if (multiple) {
      if (!getNestedPropertyValue(newValues, name).includes(value.id ? value.id : value)) {
        const newValue = [...getNestedPropertyValue(newValues, name), value.id ? value.id : value];
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      } else {
        const newValue = [...getNestedPropertyValue(newValues, name)];
        const index = newValue.indexOf(value.id ? value.id : value);
        newValue.splice(index, 1);
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      }
    } else {
      if (name.includes(".")) {
        const updatedValues = setNestedPropertyValue(newValues, name, value);
        setFormValues(updatedValues);
      } else {
        newValues[name] = value.id ? value.id : value;
        setFormValues(newValues);
      }
    }

    if (blur) {
      await saveOnBlur(name, value.id ? value.id : value, multiple);
    }
  };

  const formHasEmptyFields = () => {
    if (!project) {
      const emptyOptionInputs = [
        formValues.companyId,
        formValues.investmentAddress,
        formValues.designers.length,
        formValues.orderNumber,
        formValues.conditionsNumber,
        formValues.originalDueDate,
        formValues.cost
      ].filter(item => !item);
      return !!emptyOptionInputs.length;
    } else {
      return false;
    }
  };

  const formHasInvalidAppendices = () => {
    if (!!project) {
      const invalidAppendices = formValues.appendices
        .filter(item => !item.filingDate);
      return !!invalidAppendices.length
    } else {
      return false;
    }
  };

  const saveOnBlur = async (name, value, multiple) => {
    if (!!project) {
      const nameArray = name.split('.');
      let newValue = nameArray.length ? {[nameArray[0]]: formValues[nameArray[0]]} : {};

      if (multiple) {
        if (!getNestedPropertyValue(newValue, name).includes(value.id ? value.id : value)) {
          const updatedValue = [...getNestedPropertyValue(newValue, name), value.id ? value.id : value];
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        } else {
          const updatedValue = [...getNestedPropertyValue(newValue, name)];
          const index = updatedValue.indexOf(value.id ? value.id : value);
          updatedValue.splice(index, 1);
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        }
      } else {
        if (name.includes(".")) {
          newValue = setNestedPropertyValue(newValue, name, value);
        } else {
          newValue[name] = value;
        }
      }
      const result = await updateStoenProject(newValue, project?.id, project?.version, true);

      if (result.status && result.status === 412) {
        const newProjectObj = await getSingleStoenProject(project.id);

        const currentValue = getNestedPropertyValue(project, name);
        const updatedValue = getNestedPropertyValue(newProjectObj, name);

        if (_.isEqual(currentValue, updatedValue)) {
          let updatedNewValue = nameArray.length ? {[nameArray[0]]: newProjectObj[nameArray[0]]} : {};
          if (name.includes(".")) {
            updatedNewValue = setNestedPropertyValue(updatedNewValue, name, value);
          } else {
            updatedNewValue[name] = value;
          }
          await updateStoenProject(updatedNewValue, project?.id, newProjectObj?.version, true);
          const updatedProjectObj = await getSingleStoenProject(project.id);
          await refreshSelectedProject(updatedProjectObj);
          displayToast(
            'info',
            t("Project data has been updated by other user. The form data has been updated.", "asIs"),
            7000
          );
        } else {
          await refreshSelectedProject(newProjectObj);
          await changeValue(name, updatedValue);
          displayToast(
            'error',
            t(
              "The value which you are trying to edit has been updated by other user. The form data has been updated. Please try again if required.",
              "asIs"
            ),
            0,
            true
          );
        }
      } else {
        const newProjectObj = await getSingleStoenProject(project.id);
        await refreshSelectedProject(newProjectObj);
      }
    }
  };

  if (pending) return <div className='loaderWrapper'><Loader /></div>

  return (
    <form className='StoenProjectForm'>
      {!!addProjectPending && (
        <LoaderFullPage  />
      )}
      <div className='formInputs'>
        <SectionBox label={t('header')} expandedByDefault>
          <SelectInput
            name='companyId'
            label={t('subcompany', 'asIs')}
            options={subcompanyOptions.length ? subcompanyOptions : []}
            onSelect={changeValue}
            value={formValues.companyId}
            placeholder={t('select')}
            required
            disabled={preview}
          />
          <TextInput
            label={t('google drive link')}
            name='googleDriveLink'
            onChange={changeValue}
            value={formValues.googleDriveLink}
            disabled={preview}
            link
          />
          <TextInput
            label={t('investment address')}
            required
            name='investmentAddress'
            onChange={changeValue}
            value={formValues.investmentAddress}
            disabled={preview}
          />
          <SelectInput
            name='designers'
            label={t('designer')}
            options={usersOptions}
            onSelect={changeValue}
            value={formValues.designers}
            placeholder={t('select')}
            required
            multiple
            disabled={preview}
          />
          <TextInput
            label={t('order number')}
            required
            name='orderNumber'
            onChange={changeValue}
            value={formValues.orderNumber}
            disabled={preview}
          />
          <TextInput
            label={t('conditions number')}
            required
            name='conditionsNumber'
            onChange={changeValue}
            value={formValues.conditionsNumber}
            disabled={preview}
          />
          <DateInput
            label={t('due date')}
            required={!project || !project.appendices.length}
            name={!project || !project.appendices.length ? 'originalDueDate' : 'dueDate'}
            value={!project || !project.appendices.length ? formValues.originalDueDate: formValues.dueDate}
            onChange={changeValue}
            disabled={preview || !!project?.appendices?.length}
          />
          <TextInput
            type='number'
            label={t('order cost')}
            required
            name='cost'
            onChange={changeValue}
            value={formValues.cost}
            disabled={preview}
          />
          <div className='groupBox'>
            <div className='groupBoxLabel'>
              {t('client contact details')}
            </div>
            <TextInput
              label={t('address')}
              name='clientContactDetails.address'
              onChange={changeValue}
              value={formValues.clientContactDetails.address}
              disabled={preview}
            />
            <TextInput
              label={t('phone number')}
              name='clientContactDetails.phoneNumber'
              onChange={changeValue}
              value={formValues.clientContactDetails.phoneNumber}
              disabled={preview}
            />
            <TextInput
              label={t('e-mail')}
              name='clientContactDetails.email'
              onChange={changeValue}
              value={formValues.clientContactDetails.email}
              disabled={preview}
            />
            <TextInput
              label={t('client name')}
              name='clientContactDetails.fullName'
              onChange={changeValue}
              value={formValues.clientContactDetails.fullName}
              disabled={preview}
            />
          </div>
          <TextInput
            label={t('remarks')}
            required
            name='comments'
            onChange={changeValue}
            value={formValues.comments}
            disabled={preview}
            area
          />
          {!!project && (
            <>
              <TextInput
                label={t('investment supervisor')}
                required
                name='investmentSupervisor'
                onChange={changeValue}
                value={formValues.investmentSupervisor}
                disabled={preview}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('partial project acceptance')}
                </div>
                <CheckboxInput
                  label={t('completed')}
                  name='partialProjectAcceptance.complete'
                  value={formValues.partialProjectAcceptance.complete}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('date')}
                  name='partialProjectAcceptance.date'
                  value={formValues.partialProjectAcceptance.date}
                  onChange={changeValue}
                  disabled={preview}
                />
                <TextInput
                  label={t('comment')}
                  name='partialProjectAcceptance.comment'
                  value={formValues.partialProjectAcceptance.comment}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('network standards')}
                </div>
                <CheckboxInput
                  label={t('completed')}
                  name='networkStandards.complete'
                  value={formValues.networkStandards.complete}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('date')}
                  name='networkStandards.date'
                  value={formValues.networkStandards.date}
                  onChange={changeValue}
                  disabled={preview}
                />
                <TextInput
                  label={t('comment')}
                  name='networkStandards.comment'
                  value={formValues.networkStandards.comment}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              {!!project && !!project.projectStateChanges.length && (
                <ChangeHistory
                  stateChangeHistory={project.projectStateChanges}
                  changeLog={project.stoenChangelogItems}
                  usersOptions={usersOptions}
                />
              )}
            </>
          )}
        </SectionBox>

        {!!project && (
          <>
            <div className='mainSectionLabel'>
              {t('appendices')}
            </div>
            <SectionBox label={t('appendices')}>
              <Plus
                name='appendices'
                value={formValues.appendices}
                itemObject={{appendixDate: '', dueDate: '', googleDriveLink: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'appendixDate', label: t('appendix date'), required: false},
                  {type: 'date', name: 'dueDate', label: t('due date'), required: false},
                  {type: 'text', name: 'googleDriveLink', label: t('google drive link'), required: false, link: true},,
                  {type: 'date', name: 'filingDate', label: t('application submission date'), required: true},
                  {type: 'text', name: 'comment', label: t('remarks'), required: false}
                ]}
              />
              {formHasInvalidAppendices() && (
                <div className='sectionError'>{t('all appendices must have filing date filled in')}</div>
              )}
            </SectionBox>
          </>
        )}

        {!!project && (
          <>
            <div className='mainSectionLabel'>
              {t('project')}
            </div>
            <SectionBox label={t('geodesy')}>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('ground registry extract')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.geodesy.groundsRegistryExtract.inDate'
                  value={formValues.stoenProject.geodesy.groundsRegistryExtract.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.geodesy.groundsRegistryExtract.outDate'
                  value={formValues.stoenProject.geodesy.groundsRegistryExtract.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('base map')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.geodesy.baseMap.inDate'
                  value={formValues.stoenProject.geodesy.baseMap.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.geodesy.baseMap.outDate'
                  value={formValues.stoenProject.geodesy.baseMap.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('project map')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.geodesy.projectMap.inDate'
                  value={formValues.stoenProject.geodesy.projectMap.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.geodesy.projectMap.outDate'
                  value={formValues.stoenProject.geodesy.projectMap.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <TextInput
                  label={t('surveyor')}
                  name='stoenProject.geodesy.projectMap.geodesyComment'
                  onChange={changeValue}
                  value={formValues.stoenProject.geodesy.projectMap.geodesyComment}
                  disabled={preview}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('stoen inventory', 'asIs')}>
              <DateInput
                label={t('in date')}
                name='stoenProject.inventory.inDate'
                value={formValues.stoenProject.inventory.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.inventory.outDate'
                value={formValues.stoenProject.inventory.outDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('location agreement with client')}>
              <DateInput
                label={t('in date')}
                name='stoenProject.clientLocationAgreement.inDate'
                value={formValues.stoenProject.clientLocationAgreement.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.clientLocationAgreement.outDate'
                value={formValues.stoenProject.clientLocationAgreement.outDate}
                onChange={changeValue}
                disabled={preview}
              />
              <TextInput
                label={t('comment')}
                name='stoenProject.clientLocationAgreement.comment'
                value={formValues.stoenProject.clientLocationAgreement.comment}
                onChange={changeValue}
                disabled={preview}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('received original')}
                </div>
                  <CheckboxInput
                    label={t('completed')}
                    name='stoenProject.clientLocationAgreement.receivedOriginal.complete'
                    value={formValues.stoenProject.clientLocationAgreement.receivedOriginal.complete}
                    onChange={changeValue}
                    disabled={preview}
                  />
                <DateInput
                  label={t('date')}
                  name='stoenProject.clientLocationAgreement.receivedOriginal.date'
                  value={formValues.stoenProject.clientLocationAgreement.receivedOriginal.date}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('received adaptation project')}
                </div>
                <CheckboxInput
                  label={t('completed')}
                  name='stoenProject.clientLocationAgreement.receivedAdaptationProject.complete'
                  value={formValues.stoenProject.clientLocationAgreement.receivedAdaptationProject.complete}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('date')}
                  name='stoenProject.clientLocationAgreement.receivedAdaptationProject.date'
                  value={formValues.stoenProject.clientLocationAgreement.receivedAdaptationProject.date}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('stoen devices location agreement', 'asIs')}>
              <DateInput
                label={t('in date')}
                name='stoenProject.stoenLocationAgreement.inDate'
                value={formValues.stoenProject.stoenLocationAgreement.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.stoenLocationAgreement.outDate'
                value={formValues.stoenProject.stoenLocationAgreement.outDate}
                onChange={changeValue}
                disabled={preview}
              />
              <TextInput
                label={t('comment')}
                name='stoenProject.stoenLocationAgreement.comment'
                value={formValues.stoenProject.stoenLocationAgreement.comment}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('zud', 'upper')}>
              <DateInput
                label={t('in date')}
                name='stoenProject.zud.inDate'
                value={formValues.stoenProject.zud.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.zud.outDate'
                value={formValues.stoenProject.zud.outDate}
                onChange={changeValue}
                disabled={preview}
              />
              <Plus
                name='stoenProject.zud.details'
                value={formValues.stoenProject.zud.details}
                itemObject={{name: '', inDate: '', outDate: '', comment: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'name', label: t('name'), required: false},
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false},
                  {type: 'text', name: 'comment', label: t('comment'), required: false}
                ]}
              />
            </SectionBox>
            <SectionBox label={t('rights to the location')}>
              <Plus
                name='stoenProject.rightsToTheLocation'
                value={formValues.stoenProject.rightsToTheLocation}
                itemObject={{parcelNumber: '', documentType: '', inDate: '', outDate: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'parcelNumber', label: t('parcel number'), required: false},
                  {type: 'text', name: 'documentType', label: t('document type'), required: false},
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false}
                ]}
              />
            </SectionBox>
            <SectionBox label={t('por', 'upper')}>
              <Plus
                name='stoenProject.por'
                value={formValues.stoenProject.por}
                itemObject={{inDate: '', outDate: '', projectRecipient: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false},
                  {type: 'text', name: 'projectRecipient', label: t('project recipient'), required: false},
                ]}
              />
            </SectionBox>
            <SectionBox label={t('additional agreements')}>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('restorer')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.additionalAgreements.restorer.inDate'
                  value={formValues.stoenProject.additionalAgreements.restorer.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.additionalAgreements.restorer.outDate'
                  value={formValues.stoenProject.additionalAgreements.restorer.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('guarantee')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.additionalAgreements.guarantee.inDate'
                  value={formValues.stoenProject.additionalAgreements.guarantee.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.additionalAgreements.guarantee.outDate'
                  value={formValues.stoenProject.additionalAgreements.guarantee.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <TextInput
                  label={t('person')}
                  name='stoenProject.additionalAgreements.guarantee.name'
                  value={formValues.stoenProject.additionalAgreements.guarantee.name}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('surface restoration project')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='stoenProject.additionalAgreements.surfaceRestorationProject.inDate'
                  value={formValues.stoenProject.additionalAgreements.surfaceRestorationProject.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='stoenProject.additionalAgreements.surfaceRestorationProject.outDate'
                  value={formValues.stoenProject.additionalAgreements.surfaceRestorationProject.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <Plus
                name='stoenProject.additionalAgreements.additional'
                value={formValues.stoenProject.additionalAgreements.additional}
                itemObject={{comment: '', inDate: '', outDate: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'comment', label: t('description'), required: false},
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false},
                ]}
              />
            </SectionBox>
            <SectionBox label={t('land rights declaration', 'asIs')}>
              <DateInput
                label={t('in date')}
                name='stoenProject.landRightsDeclaration.inDate'
                value={formValues.stoenProject.landRightsDeclaration.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.landRightsDeclaration.outDate'
                value={formValues.stoenProject.landRightsDeclaration.outDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('project agreement')}>
              <TextInput
                label={t('side')}
                name='stoenProject.projectAgreement.side'
                value={formValues.stoenProject.projectAgreement.side}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('in date')}
                name='stoenProject.projectAgreement.inDate'
                value={formValues.stoenProject.projectAgreement.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='stoenProject.projectAgreement.outDate'
                value={formValues.stoenProject.projectAgreement.outDate}
                onChange={changeValue}
                disabled={preview}
              />
              <Plus
                label={t('comments')}
                name='stoenProject.projectAgreement.comments'
                value={formValues.stoenProject.projectAgreement.comments}
                itemObject={{comment: '', inDate: '', outDate: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'comment', label: t('comment'), required: false},
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false},
                ]}
              />
            </SectionBox>
          </>
        )}
      </div>
      <div className='formButtons'>
        <Button type='secondary' action={secondaryButtonAction} label={t('close')} />
        {!!primaryButtonAction && !project && (
          <Button
            type='primary'
            action={() => primaryButtonAction(formValues, project?.id, project?.version)}
            label={!project ? t('add project'): t('save')}
            margin='left'
            disabled={addProjectPending || formHasEmptyFields() || formHasInvalidAppendices()}
            pending={addProjectPending}
          />
        )}
      </div>
    </form>
  );
};

export default StoenProjectForm;
